import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { RequirementStatusCategory } from '../../models/requirement.model';
import { SelectionListDefault, SelectionListKey, SelectionListWert } from '../../models/selection-list-default.model';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { SectionTitleComponent } from '../section-title/section-title.component';

export type AuswahlListe = SelectionListDefault;

export interface FeldFehler {
  listKey: SelectionListKey;
  index: number | null;
  code: string;
}

export interface WerteAenderung {
  listKey: SelectionListKey;
  werte: SelectionListWert[];
}

export type WertAktion =
  | { art: 'hinzufuegen'; value: string }
  | { art: 'aendern'; index: number; value: string }
  | { art: 'festlegen'; index: number }
  | { art: 'entfernen'; index: number }
  | { art: 'verschieben'; von: number; nach: number };

const KATEGORIEN: readonly RequirementStatusCategory[] = ['OPEN', 'IN_PROGRESS', 'REVIEW', 'APPROVED', 'DONE'];

const LISTEN_NAMEN: Record<SelectionListKey, string> = {
  'requirement-status': 'requirementStatus',
  'requirement-type': 'requirementType',
  'requirement-priority': 'requirementPriority',
};

const FEHLER_JE_CODE: Record<string, string> = {
  VALUE_BLANK: 'leer',
  VALUE_DUPLICATE: 'doppelt',
  VALUE_TOO_LONG: 'zuLang',
  CATEGORY_REQUIRED: 'kategorie',
  CATEGORY_INVALID: 'kategorie',
  OPEN_CATEGORY_REQUIRED: 'ohneOffen',
};

const normiert = (wert: string) => wert.trim().toLowerCase();

/** Je Zeile der i18n-Schlüssel ihres Fehlers; ein Duplikat wird wie im Backend am späteren Vorkommen gemeldet. */
export function zeilenFehler(werte: SelectionListWert[]): (string | null)[] {
  return werte.map((w, i) => {
    if (!w.value.trim()) return 'auswahllisten.fehler.leer';
    const doppelt = werte.slice(0, i).some((v) => normiert(v.value) === normiert(w.value));
    return doppelt ? 'auswahllisten.fehler.doppelt' : null;
  });
}

export function fehltOffen(listKey: SelectionListKey, werte: SelectionListWert[]): boolean {
  return listKey === 'requirement-status' && !werte.some((w) => w.category === 'OPEN');
}

export function listeGueltig(listKey: SelectionListKey, werte: SelectionListWert[]): boolean {
  return werte.length > 0 && zeilenFehler(werte).every((f) => f === null) && !fehltOffen(listKey, werte);
}

export function gleicheWerte(a: SelectionListWert[], b: SelectionListWert[]): boolean {
  return (
    a.length === b.length &&
    a.every((w, i) => w.value.trim() === b[i].value.trim() && (w.category ?? null) === (b[i].category ?? null))
  );
}

@Component({
  selector: 'app-auswahllisten-editor',
  standalone: true,
  imports: [
    CdkDrag,
    CdkDragHandle,
    CdkDropList,
    ConfirmDialogComponent,
    MatIcon,
    SearchBarComponent,
    SectionTitleComponent,
    TranslatePipe,
  ],
  templateUrl: './auswahllisten-editor.component.html',
  styleUrl: './auswahllisten-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuswahllistenEditorComponent {
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);

  readonly listen = input.required<AuswahlListe[]>();
  readonly modus = input<'mandant' | 'projekt'>('mandant');
  readonly laedt = input(false);
  readonly feldFehler = input<FeldFehler | null>(null);
  readonly werteChange = output<WerteAenderung>();
  readonly wertAktion = output<WertAktion>();

  readonly kategorien = KATEGORIEN;
  readonly wertMax = FIELD_LENGTHS.CATALOG_VALUE_MAX;
  readonly suche = signal('');
  readonly neu = signal('');
  readonly fragen = signal(false);
  readonly ansage = signal('');
  private readonly aktivKey = signal<SelectionListKey | null>(null);
  private readonly griffe = viewChildren<ElementRef<HTMLButtonElement>>('griff');
  private readonly sprache = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly aktiv = computed<AuswahlListe | null>(() => {
    const listen = this.listen();
    return listen.find((l) => l.listKey === this.aktivKey()) ?? listen[0] ?? null;
  });

  readonly istStatus = computed(() => this.aktiv()?.listKey === 'requirement-status');

  readonly istProjekt = computed(() => this.modus() === 'projekt');

  readonly anzahlAngepasst = computed(() => this.listen().filter((l) => l.angepasst).length);

  readonly gruppen = computed(() => {
    this.sprache();
    const suche = normiert(this.suche());
    const treffer = this.listen().filter((l) => !suche || this.suchtext(l).includes(suche));
    return [...new Set(treffer.map((l) => l.bereich))].map((bereich) => ({
      bereich,
      listen: treffer.filter((l) => l.bereich === bereich),
    }));
  });

  readonly fehler = computed(() => {
    const aktiv = this.aktiv();
    if (!aktiv) return [];
    const fehler = zeilenFehler(aktiv.werte);
    const server = this.feldFehler();
    if (server?.listKey === aktiv.listKey && server.index !== null && server.index < fehler.length) {
      fehler[server.index] ??= this.fehlerSchluessel(server.code);
    }
    return fehler;
  });

  readonly listenFehler = computed(() => {
    const aktiv = this.aktiv();
    if (!aktiv) return null;
    if (fehltOffen(aktiv.listKey, aktiv.werte)) return 'auswahllisten.fehler.ohneOffen';
    const server = this.feldFehler();
    return server?.listKey === aktiv.listKey && server.index === null ? this.fehlerSchluessel(server.code) : null;
  });

  readonly doppelt = computed(() => {
    const neu = normiert(this.neu());
    return !!neu && !!this.aktiv()?.werte.some((w) => normiert(w.value) === neu);
  });

  readonly kannHinzufuegen = computed(() => !!this.neu().trim() && !this.doppelt());

  readonly abweichendVomStandard = computed(() => {
    const aktiv = this.aktiv();
    return !!aktiv && !gleicheWerte(aktiv.werte, aktiv.standard);
  });

  constructor() {
    effect(() => {
      const fehler = this.feldFehler();
      if (fehler) untracked(() => this.aktivKey.set(fehler.listKey));
    });
  }

  nameSchluessel(listKey: SelectionListKey): string {
    return `auswahllisten.liste.${LISTEN_NAMEN[listKey]}.name`;
  }

  verwendetSchluessel(listKey: SelectionListKey): string {
    return `auswahllisten.liste.${LISTEN_NAMEN[listKey]}.verwendetIn`;
  }

  waehlen(listKey: SelectionListKey): void {
    this.aktivKey.set(listKey);
    this.neu.set('');
  }

  umbenennen(index: number, value: string): void {
    this.aendern(this.werte().map((w, i) => (i === index ? { ...w, value } : w)));
    this.wertAktion.emit({ art: 'aendern', index, value });
  }

  festlegen(index: number): void {
    this.wertAktion.emit({ art: 'festlegen', index });
  }

  wertTaste(index: number, event: Event): void {
    event.preventDefault();
    this.festlegen(index);
  }

  kategorieSetzen(index: number, category: string): void {
    this.aendern(
      this.werte().map((w, i) => (i === index ? { ...w, category: category as RequirementStatusCategory } : w)),
    );
  }

  entfernen(index: number): void {
    if (this.werte().length <= 1) return;
    this.aendern(this.werte().filter((_, i) => i !== index));
    this.wertAktion.emit({ art: 'entfernen', index });
  }

  hinzufuegen(): void {
    if (!this.kannHinzufuegen()) return;
    const value = this.neu().trim();
    this.aendern([...this.werte(), this.istStatus() ? { value, category: 'OPEN' } : { value }]);
    this.wertAktion.emit({ art: 'hinzufuegen', value });
    this.neu.set('');
  }

  neuTaste(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.hinzufuegen();
  }

  fallenLassen(event: CdkDragDrop<unknown>): void {
    if (event.previousIndex !== event.currentIndex) this.verschieben(event.previousIndex, event.currentIndex);
  }

  griffTaste(index: number, event: KeyboardEvent): void {
    if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
    event.preventDefault();
    const ziel = event.key === 'ArrowUp' ? index - 1 : index + 1;
    const anzahl = this.werte().length;
    if (ziel < 0 || ziel >= anzahl) return;
    const wert = this.werte()[index].value;
    this.verschieben(index, ziel);
    this.ansage.set(this.translate.instant('auswahllisten.ansage', { wert, n: ziel + 1, m: anzahl }));
    afterNextRender(() => this.griffe()[ziel]?.nativeElement.focus(), { injector: this.injector });
  }

  zuruecksetzen(): void {
    this.fragen.set(false);
    const aktiv = this.aktiv();
    if (aktiv) this.aendern(aktiv.standard);
  }

  private verschieben(von: number, nach: number): void {
    const werte = [...this.werte()];
    moveItemInArray(werte, von, nach);
    this.aendern(werte);
    this.wertAktion.emit({ art: 'verschieben', von, nach });
  }

  private werte(): SelectionListWert[] {
    return this.aktiv()?.werte ?? [];
  }

  private aendern(werte: SelectionListWert[]): void {
    const aktiv = this.aktiv();
    if (aktiv) this.werteChange.emit({ listKey: aktiv.listKey, werte });
  }

  private suchtext(liste: AuswahlListe): string {
    return [
      `auswahllisten.bereich.${liste.bereich}`,
      this.nameSchluessel(liste.listKey),
      this.verwendetSchluessel(liste.listKey),
    ]
      .map((schluessel) => this.translate.instant(schluessel) as string)
      .join(' ')
      .toLowerCase();
  }

  private fehlerSchluessel(code: string): string {
    return `auswahllisten.fehler.${FEHLER_JE_CODE[code] ?? 'liste'}`;
  }
}
